import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Download, ExternalLink, Loader2, Mail, MapPin, Monitor, Phone, Share2, ShieldCheck, Sparkles, Star, Store, Tv, Users, X, ZoomIn } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import EnterpriseShell from "../components/public/EnterpriseShell";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE, formatErr, resolveMediaUrl } from "../lib/api";
import { findBySlug, publicItems, resolveWebsiteImage, useWebsiteContent, whatsappUrl } from "../lib/websiteContent";
import { useSeo } from "../lib/seo";

const iconMap = [Monitor, Tv, ShieldCheck, Store, Users, Sparkles];

function sameProductCategoryName(product, category) {
  const clean = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  return clean(product?.title) === clean(category);
}

function productCategorySlug(value) {
  return String(value || "category").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "category";
}

function useEnterpriseSeo(pageKey, fallback) {
  useSeo(pageKey, fallback);
}

export function EnterpriseHome() {
  const { data } = useWebsiteContent();
  const settings = data.settings || {};
  const home = data.home || {};
  useEnterpriseSeo("home", {
    metaTitle: "RP Infotech | LED TV, LED Active Display, Interactive Panel & Digital Signage",
    metaDescription: "RP Infotech provides commercial LED TVs, LED active display video walls, LED interactive panels for classrooms, LED floor stand and wall mounted displays, powered by the RP Signage cloud platform.",
  });
  const products = publicItems(data.products).slice(0, 10);
  const industries = publicItems(data.industries).slice(0, 8);
  const heroImage = resolveWebsiteImage(!home.heroImage || home.heroImage === "/banner.png" ? "/rp-signage-hero-banner.png" : home.heroImage);
  const primaryCta = home.primaryCta || "Book Live Demo";
  const secondaryCta = home.secondaryCta || "View Products";
  const thirdCta = home.thirdCta || "Contact Sales";
  return (
    <EnterpriseShell content={data}>
      <section className="relative overflow-hidden bg-white text-[#07142f]">
        <h1 className="sr-only">
          {home.heroTitle || "LED Displays & Digital Signage Solutions for Every Business"}
        </h1>
        <div className="hidden bg-white lg:block">
          <div className="w-full bg-white">
            <img
              src={heroImage}
              alt="RP Signage LED display and digital signage solutions"
              className="block h-auto w-full object-contain object-top"
            />
          </div>
          <div className="bg-white">
            <div className="mx-auto flex w-full max-w-[1400px] flex-wrap justify-center gap-3 px-8 py-5">
              <Link to="/contact#demo" className="rounded-xl bg-blue-600 px-6 py-4 text-center font-black text-white shadow-lg shadow-blue-600/30">{primaryCta}</Link>
              <Link to="/products" className="rounded-xl border border-slate-200 bg-slate-950 px-6 py-4 text-center font-black text-white shadow-lg shadow-slate-950/10">{secondaryCta} <ArrowRight className="ml-2 inline h-4 w-4" /></Link>
              <a href={whatsappUrl(settings)} className="rounded-xl border border-orange-400 bg-white px-6 py-4 text-center font-black text-orange-600 shadow-lg">{thirdCta}</a>
            </div>
          </div>
        </div>
        <div className="mx-auto grid min-h-[82vh] max-w-[1400px] items-center gap-7 px-4 py-12 lg:hidden">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-orange-600">RP Signage</p>
            <h2 className="mt-3 text-4xl font-black leading-tight tracking-tight">
              {home.heroTitle || "LED Displays & Digital Signage Solutions for Every Business"}
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600">{home.heroSubtitle}</p>
            <div className="mt-7 grid gap-3">
              <Link to="/contact#demo" className="rounded-xl bg-blue-600 px-6 py-4 text-center font-black text-white shadow-lg shadow-blue-600/30">{primaryCta}</Link>
              <Link to="/products" className="rounded-xl border border-slate-200 px-6 py-4 text-center font-black">{secondaryCta}</Link>
              <a href={whatsappUrl(settings)} className="rounded-xl border border-orange-400 px-6 py-4 text-center font-black text-orange-600">{thirdCta}</a>
            </div>
            <div className="mt-8 grid gap-3 text-sm font-semibold">
              {["Cloud Based Platform", "Easy to Use & Manage", "Reliable Support 24/7"].map((item) => <div key={item} className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-orange-500" />{item}</div>)}
            </div>
          </div>
          <img src={heroImage} alt="RP Signage display solution banner" className="aspect-[16/10] w-full rounded-2xl object-cover shadow-2xl" />
        </div>
      </section>

      <Stats stats={home.stats} />
      <ProductStrip products={products} />
      <SplitShowcase industries={industries} products={products} />
      <WhyChooseSection />
      <CardGrid title="Industries We Serve" subtitle="Digital signage and LED displays suited to every business vertical." items={industries} base="/industries" />
      <HowItWorksSection />
      <Testimonials rows={data.testimonials} />
      <Faq rows={data.faqs} />
      <Cta settings={settings} />
    </EnterpriseShell>
  );
}

function WhyChooseSection() {
  const points = [
    { title: "One Vendor, Full Stack", description: "Software, LED hardware and installation from a single team instead of juggling separate vendors." },
    { title: "Built for Multi-Location Business", description: "Manage one screen or a chain of locations from the same cloud dashboard." },
    { title: "Local Installation & Support", description: "On-site installation and support across Vasai, Virar, Nalasopara, Mumbai, Thane and Palghar." },
    { title: "Dealer-Friendly Model", description: "Dealer panel and product support for partners who install and resell RP Signage." },
  ];
  return (
    <section className="bg-slate-50">
      <div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <h2 className="text-center text-3xl font-black text-slate-950">Why Businesses Choose RP Infotech</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {points.map((point) => (
            <div key={point.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <CheckCircle2 className="h-6 w-6 text-blue-600" />
              <div className="mt-3 font-black text-slate-950">{point.title}</div>
              <p className="mt-2 text-sm text-slate-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { title: "Tell Us Your Requirement", description: "Share your space, business type and screen requirement via the contact form, WhatsApp or a call." },
    { title: "Site Survey & Recommendation", description: "Our team assesses the site and recommends the right LED display, mount and content setup." },
    { title: "Installation & Configuration", description: "Certified technicians install and configure the display, connected to the RP Signage platform." },
    { title: "Go Live & Ongoing Support", description: "Start publishing content, with ongoing support and AMC options available." },
  ];
  return (
    <section className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
      <h2 className="text-center text-3xl font-black text-slate-950">How It Works</h2>
      <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, index) => (
          <li key={step.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-black text-white">{index + 1}</span>
            <div className="mt-3 font-black text-slate-950">{step.title}</div>
            <p className="mt-2 text-sm text-slate-600">{step.description}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function CategoryLanding({ category, settings }) {
  if (!category) return null;
  const hasBody = (category.benefits && category.benefits.length) || (category.technologies && category.technologies.length)
    || (category.applications && category.applications.length) || (category.industriesServed && category.industriesServed.length)
    || (category.whyChooseUs && category.whyChooseUs.length) || (category.installationProcess && category.installationProcess.length)
    || (category.faqs && category.faqs.length);
  if (!hasBody) return null;
  const inquiry = whatsappUrl(settings, `Hello RP Infotech, I would like to know more about ${category.name}.`);
  return (
    <section className="mx-auto mt-10 grid max-w-[1400px] gap-8 px-4 lg:px-8">
      {category.benefits && category.benefits.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Why {category.name}?</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {category.benefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-2 rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" /> {benefit}
              </div>
            ))}
          </div>
        </div>
      )}

      {category.technologies && category.technologies.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Key Technologies</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {category.technologies.map((tech) => <span key={tech} className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700">{tech}</span>)}
          </div>
        </div>
      )}

      {category.applications && category.applications.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Applications</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {category.applications.map((app) => (
              <div key={app.title} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="font-black text-slate-900">{app.title}</div>
                <p className="mt-1 text-sm text-slate-600">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {category.industriesServed && category.industriesServed.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Industries Served</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {category.industriesServed.map((industry) => <span key={industry} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">{industry}</span>)}
          </div>
        </div>
      )}

      {category.installationProcess && category.installationProcess.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Installation Process</h2>
          <ol className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {category.installationProcess.map((step, index) => (
              <li key={step.title} className="rounded-xl border border-slate-100 p-4">
                <div className="flex items-center gap-2 text-sm font-black text-blue-700"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs text-white">{index + 1}</span>{step.title}</div>
                <p className="mt-2 text-sm text-slate-600">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {category.whyChooseUs && category.whyChooseUs.length > 0 && (
        <div className="rounded-3xl bg-[#07142f] p-6 text-white">
          <h2 className="text-2xl font-black">Why Choose RP Infotech</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {category.whyChooseUs.map((reason) => (
              <div key={reason} className="flex items-start gap-2 text-sm font-semibold text-slate-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-400" /> {reason}
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={inquiry} target="_blank" rel="noreferrer" className="inline-flex rounded-xl bg-green-600 px-5 py-3 font-black">WhatsApp Enquiry</a>
            <Link to="/contact#demo" className="inline-flex rounded-xl bg-orange-500 px-5 py-3 font-black">Request Quotation</Link>
            {category.catalogueUrl ? <a href={category.catalogueUrl} target="_blank" rel="noreferrer" className="inline-flex rounded-xl border border-white/30 px-5 py-3 font-black">Download Catalogue</a> : null}
          </div>
        </div>
      )}

      {category.faqs && category.faqs.length > 0 && (
        <div>
          <h2 className="text-2xl font-black text-slate-950">Frequently Asked Questions</h2>
          <div className="mt-4 grid gap-3">
            {category.faqs.map((row) => (
              <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="cursor-pointer font-black">{row.question}</summary>
                <p className="mt-3 text-slate-600">{row.answer}</p>
              </details>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function ProductsPage() {
  const { data } = useWebsiteContent();
  const [searchParams, setSearchParams] = useSearchParams();
  const products = publicItems(data.products);
  const allProductCategoryDetails = Array.isArray(data.productCategories) ? data.productCategories : [];
  const hiddenCategoryNames = new Set(allProductCategoryDetails.filter((category) => category?.active === false).map((category) => category.name).filter(Boolean));
  const productCategoryDetails = publicItems(allProductCategoryDetails).sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0));
  const categoryDetailMap = new Map(productCategoryDetails.map((category) => [category.name, category]));
  const categoryNames = [
    ...productCategoryDetails.map((category) => category.name).filter(Boolean),
    ...products.map((item) => item.category).filter(Boolean),
  ].filter((name, index, all) => !hiddenCategoryNames.has(name) && all.indexOf(name) === index);
  const categories = ["All Products", ...categoryNames];
  const selectedCategory = searchParams.get("category") || "All Products";
  const selectedCategoryDetail = selectedCategory !== "All Products" ? categoryDetailMap.get(selectedCategory) : null;
  useEnterpriseSeo(
    selectedCategoryDetail ? `products-${selectedCategoryDetail.slug}` : "products",
    selectedCategoryDetail
      ? { metaTitle: selectedCategoryDetail.seoTitle || `${selectedCategoryDetail.name} | RP Infotech`, metaDescription: selectedCategoryDetail.seoDescription || selectedCategoryDetail.summary || "" }
      : { metaTitle: "LED TVs, LED Displays & Digital Signage Products | RP Infotech" }
  );
  const categoryCards = categoryNames.map((category) => {
    const categoryDetail = categoryDetailMap.get(category);
    const rootCard = products.find((item) => item.category === category && sameProductCategoryName(item, category));
    const firstProduct = products.find((item) => item.category === category);
    return {
      ...(rootCard || {}),
      slug: rootCard?.slug || categoryDetail?.slug || `category-${productCategorySlug(category)}`,
      title: categoryDetail?.name || rootCard?.title || category,
      category,
      summary: categoryDetail?.summary || rootCard?.summary || `View ${category} products.`,
      description: categoryDetail?.description || rootCard?.description || "",
      image: categoryDetail?.image || rootCard?.image || firstProduct?.image || "/banner.png",
      features: categoryDetail?.features || rootCard?.features || [],
      modelNumber: rootCard?.modelNumber || "",
      technicalSpecifications: rootCard?.technicalSpecifications || [],
      active: categoryDetail?.active ?? rootCard?.active ?? true,
      isCategoryOverview: true,
    };
  });
  const filteredProducts = selectedCategory === "All Products"
    ? categoryCards
    : products.filter((item) => item.category === selectedCategory && !sameProductCategoryName(item, selectedCategory));
  // Most categories map to exactly one product right now, so a category page would
  // otherwise just show one thin card that links to the same product's own detail
  // page - an extra click for no reason. Skip straight to the full product page
  // (specs, variants, gallery, FAQs) instead of the in-between listing.
  if (selectedCategory !== "All Products" && filteredProducts.length === 1 && filteredProducts[0]?.slug) {
    return <Navigate to={`/products/${filteredProducts[0].slug}`} replace />;
  }
  const chooseCategory = (category) => {
    setSearchParams(category === "All Products" ? {} : { category });
  };
  const heroTitle = (selectedCategoryDetail && (selectedCategoryDetail.heroTitle || selectedCategoryDetail.name)) || "Our Products";
  const heroSubtitle = (selectedCategoryDetail && (selectedCategoryDetail.heroSubtitle || selectedCategoryDetail.summary)) || "Powerful Hardware. Smart Software. Complete Solutions.";
  const heroImage = (selectedCategoryDetail && selectedCategoryDetail.image) || "/placeholders/products-banner.svg";
  return (
    <EnterpriseShell content={data}>
      <nav aria-label="Breadcrumb" className="mx-auto max-w-[1400px] px-4 pt-4 text-xs font-bold text-slate-500 lg:px-8">
        <ol className="flex flex-wrap items-center gap-1.5 [&>li+li]:before:mr-1.5 [&>li+li]:before:content-['/']">
          <li><Link to="/" className="hover:text-blue-700">Home</Link></li>
          {selectedCategoryDetail ? (
            <>
              <li><Link to="/products" className="hover:text-blue-700">Products</Link></li>
              <li className="text-slate-800" aria-current="page">{selectedCategoryDetail.name}</li>
            </>
          ) : (
            <li className="text-slate-800" aria-current="page">Products</li>
          )}
        </ol>
      </nav>
      <PageHero title={heroTitle} subtitle={heroSubtitle} image={heroImage} />

      {!selectedCategoryDetail && (
        <section className="mx-auto max-w-[1400px] px-4 pt-10 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-black text-slate-950">Which Display Do You Need?</h2>
              <p className="mt-3 text-slate-600">RP Infotech's product range covers every LED display format, so the right choice usually comes down to where and how it will be used.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { title: "Commercial LED TV", use: "Showrooms, offices and retail counters where a standard flat-panel display is enough." },
                { title: "LED Active Display / Video Wall", use: "Large-format branding, advertising facades and control rooms needing a custom-size, seamless display." },
                { title: "LED Interactive Panel", use: "Classrooms and meeting rooms that need touch interaction, not just playback." },
                { title: "LED Floor Stand Display", use: "Receptions, exhibitions and retail floors where wall mounting isn't an option." },
                { title: "LED Wall Mounted Display", use: "Permanent installations in retail, hospitality or education spaces with a wall to mount on." },
              ].map((row) => (
                <div key={row.title} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="font-black text-slate-900">{row.title}</div>
                  <p className="mt-1 text-sm text-slate-600">{row.use}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto grid max-w-[1400px] gap-6 px-4 py-10 lg:grid-cols-[260px_1fr] lg:px-8">
        <aside className="h-max rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          {categories.map((cat) => {
            const isActive = cat === selectedCategory;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => chooseCategory(cat)}
                className={`w-full rounded-xl px-4 py-3 text-left text-sm font-bold transition ${isActive ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"}`}
              >
                {cat}
              </button>
            );
          })}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="font-black text-slate-950">How We Build Every Order</h3>
            <div className="mt-3 grid gap-2.5 text-sm text-slate-700">
              <div className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" /><span><span className="font-black">Custom sizes on every category</span> - LED TVs, video walls, floor stands and wall mounts are made to the size and pixel pitch your space needs, not fixed off-the-shelf units.</span></div>
              <div className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" /><span><span className="font-black">One cloud app runs everything</span> - the same RP Signage app manages content, token/queue zones and ordering across every hardware category here.</span></div>
            </div>
          </div>
          <div className="mt-4 rounded-2xl bg-blue-50 p-5">
            <h3 className="font-black">Need Help Choosing?</h3>
            <p className="mt-2 text-sm text-slate-600">Talk to an expert for the correct product and installation plan.</p>
            <a href={whatsappUrl(data.settings)} className="mt-4 block rounded-xl bg-blue-600 px-4 py-3 text-center text-sm font-black text-white">Talk to Expert</a>
          </div>
        </aside>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredProducts.map((item) => <ProductCard key={item.slug} item={item} settings={data.settings} />)}
          {!filteredProducts.length && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center font-bold text-slate-500 md:col-span-2 xl:col-span-3">
              No products found in this category.
            </div>
          )}
        </div>
      </section>
      <CategoryLanding category={selectedCategoryDetail} settings={data.settings} />
    </EnterpriseShell>
  );
}

export function ProductDetailPage() {
  const { slug } = useParams();
  const { data } = useWebsiteContent();
  const item = findBySlug(data.products, slug);
  // Categories are currently 1:1 with products, so filtering to "same category" would
  // always come back empty - fall back to other products so this section (and the
  // navigation it offers) never dead-ends.
  const sameCategoryRelated = publicItems(data.products).filter((row) => row.slug !== item?.slug && item?.category && row.category === item.category);
  const related = sameCategoryRelated.length > 0
    ? sameCategoryRelated
    : publicItems(data.products).filter((row) => row.slug !== item?.slug);
  const heroImage = resolveWebsiteImage(item?.ogImage || item?.image || data.home?.heroImage || "/rp-signage-hero-banner.png");
  const seoFallback = {
    metaTitle: item?.seoTitle || `${item?.title || "Product"} | RP Infotech`,
    metaDescription: item?.seoDescription || item?.summary || "",
    ogTitle: item?.ogTitle || item?.seoTitle || item?.title,
    ogDescription: item?.ogDescription || item?.seoDescription || item?.summary,
    ogImage: heroImage,
    twitterImage: heroImage,
    robots: item?.robotsIndex === false || item?.status === "draft" ? "noindex, nofollow" : "index, follow",
  };
  if (item?.canonicalUrl) seoFallback.canonicalUrl = item.canonicalUrl;
  if (item) seoFallback.schemaJson = JSON.stringify(productJsonLd(item, data));
  useEnterpriseSeo(slug || "product", seoFallback);
  return <WebsiteProductDetail content={data} product={item} related={related} />;
}

export function IndustriesPage() {
  const { data } = useWebsiteContent();
  useEnterpriseSeo("industries", { metaTitle: "Industries | RP Infotech Digital Signage Use Cases" });
  return <IndexPage content={data} title="Industries" subtitle="Business display solutions for hotels, clinics, schools, societies, retail, offices and more." rows={data.industries} base="/industries" image="/placeholders/industries-banner.svg" />;
}

export function IndustryDetailPage() {
  const { slug } = useParams();
  const { data } = useWebsiteContent();
  const item = findBySlug(data.industries, slug);
  useEnterpriseSeo(slug || "industry", { metaTitle: `${item?.title || "Industry"} | RP Infotech`, metaDescription: item?.summary || "" });
  return <DetailPage content={data} item={item} type="industry" base="/industries" related={data.products} />;
}

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

function contactDeviceType() {
  const ua = navigator.userAgent || "";
  if (/ipad|tablet/i.test(ua)) return "tablet";
  if (/mobile|android|iphone/i.test(ua)) return "mobile";
  return "desktop";
}

function contactSourceFromReferrer() {
  const ref = document.referrer || "";
  const lower = ref.toLowerCase();
  if (!ref) return "direct";
  if (lower.includes("google.")) return "google";
  if (lower.includes("facebook.") || lower.includes("fb.")) return "facebook";
  if (lower.includes("instagram.")) return "instagram";
  if (lower.includes("whatsapp") || lower.includes("wa.me")) return "whatsapp";
  return "referral";
}

function analyticsStorageId(store, key, prefix) {
  try {
    let value = store.getItem(key);
    if (!value) {
      value = `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      store.setItem(key, value);
    }
    return value;
  } catch {
    return `${prefix}-${Date.now()}`;
  }
}

async function getEnquiryCaptchaToken() {
  if (!RECAPTCHA_SITE_KEY) return { captchaToken: "", captchaProvider: "" };
  if (!window.grecaptcha?.execute) return { captchaToken: "", captchaProvider: "recaptcha" };
  const captchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "website_enquiry" });
  return { captchaToken, captchaProvider: "recaptcha" };
}

export function ContactPage() {
  const { data } = useWebsiteContent();
  const settings = data.settings || {};
  const [form, setForm] = useState({ name: "", mobile: "", email: "", organization: "", service: "", preferredContact: "Phone", message: "", consent: false, website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  useEnterpriseSeo("contact", {
    metaTitle: "Contact RP Infotech | LED Display & Digital Signage Enquiries",
    metaDescription: "Get in touch with RP Infotech for LED TV, LED active display, LED interactive panel, floor stand and wall mounted display enquiries, or book a live signage demo.",
  });

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || document.querySelector(`script[data-rp-recaptcha="true"]`)) return undefined;
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.dataset.rpRecaptcha = "true";
    document.head.appendChild(script);
    return undefined;
  }, []);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setNotice("");
  };

  const validate = () => {
    const digits = form.mobile.replace(/\D+/g, "");
    if (form.name.trim().length < 2) return "Please enter your name.";
    if (digits.length < 10 || digits.length > 15) return "Please enter a valid mobile number.";
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return "Please enter a valid email address.";
    if (form.message.trim().length < 10) return "Message must be at least 10 characters.";
    if (!form.consent) return "Please confirm you agree to be contacted about your enquiry.";
    return "";
  };

  const submitEnquiry = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const lastSubmit = Number(localStorage.getItem("rp_last_website_enquiry_at") || 0);
    if (Date.now() - lastSubmit < 30000) {
      setError("Please wait 30 seconds before sending another enquiry.");
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const captcha = await getEnquiryCaptchaToken();
      const pageUrl = window.location.href;
      const common = {
        pageUrl,
        source: contactSourceFromReferrer(),
        deviceType: contactDeviceType(),
        referrer: document.referrer || "",
        visitorId: analyticsStorageId(window.localStorage, "rp_analytics_visitor_id", "visitor"),
        sessionId: analyticsStorageId(window.sessionStorage, "rp_analytics_session_id", "session"),
      };
      await api.post("/website-enquiries", {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        organization: form.organization,
        service: form.service,
        message: [form.message, `Preferred contact method: ${form.preferredContact}`].filter(Boolean).join("\n"),
        honeypot: form.website,
        ...captcha,
        ...common,
      });
      localStorage.setItem("rp_last_website_enquiry_at", String(Date.now()));
      await api.post("/analytics/track-event", {
        ...common,
        userAgent: navigator.userAgent || "",
        eventType: "enquiry_submit",
        eventLabel: form.service || "Website enquiry form",
        createdAt: new Date().toISOString(),
      }).catch(() => {});
      setNotice("Your enquiry has been received. Our team will contact you soon.");
      setForm({ name: "", mobile: "", email: "", organization: "", service: "", preferredContact: "Phone", message: "", consent: false, website: "" });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail || err));
    } finally {
      setSubmitting(false);
    }
  };

  const mapQuery = encodeURIComponent(settings.address || "Vasai, Maharashtra, India");
  const contactFaqs = [
    { question: "How quickly will someone get back to me?", answer: "Our team reviews enquiries during business hours and follows up by phone, WhatsApp or email based on your preference below." },
    { question: "Can I get a live demo before deciding?", answer: "Yes - use the \"Book Live Demo\" option or mention it in your message and we will set up a demo suited to your LED display or signage requirement." },
    { question: "Do you install outside Vasai and Virar?", answer: "We install and support across Vasai, Virar, Nalasopara, Mumbai, Thane, Navi Mumbai and Palghar; mention your location and we will confirm feasibility." },
  ];

  return (
    <EnterpriseShell content={data}>
      <PageHero title="Contact RP Infotech" subtitle="Talk to our team about LED TVs, LED active displays, interactive panels, floor stand and wall mounted displays, or book a live demo." image="/placeholders/contact-banner.svg" />

      <section id="demo" className="mx-auto grid max-w-[1400px] gap-8 px-4 py-12 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <div className="grid gap-6">
          <div className="rounded-2xl bg-[#07142f] p-8 text-white">
            <h2 className="text-3xl font-black">Talk to an expert</h2>
            <div className="mt-6 grid gap-4 text-sm">
              <a href={`tel:${settings.phone}`} className="flex items-center gap-3"><Phone className="h-5 w-5 text-orange-400" />{settings.phone}</a>
              <a href={`mailto:${settings.email}`} className="flex items-center gap-3"><Mail className="h-5 w-5 text-orange-400" />{settings.email}</a>
              <div className="flex items-center gap-3"><MapPin className="h-5 w-5 flex-shrink-0 text-orange-400" />{settings.address}</div>
              <div className="flex items-center gap-3"><Clock className="h-5 w-5 flex-shrink-0 text-orange-400" />Monday - Saturday, business hours</div>
            </div>
            <a href={whatsappUrl(settings, "Hello RP Infotech, I want to book a live demo.")} className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-green-500 bg-white/5 px-5 py-4 text-center font-black text-green-400">Send on WhatsApp</a>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-black text-slate-950">Service Areas</h3>
            <p className="mt-2 text-sm text-slate-600">We supply and install across Vasai, Virar, Nalasopara, Mumbai, Thane, Navi Mumbai and Palghar.</p>
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <iframe
                title="RP Infotech service area map"
                src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                className="h-48 w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <form aria-label="Website enquiry form" className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" onSubmit={submitEnquiry}>
          <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => update("website", e.target.value)} name="website" />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Name" value={form.name} onChange={(e) => update("name", e.target.value)} maxLength={120} required />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Mobile number" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} inputMode="tel" maxLength={20} required />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} type="email" maxLength={180} />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Business / Organisation" value={form.organization} onChange={(e) => update("organization", e.target.value)} maxLength={180} />
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3" value={form.service} onChange={(e) => update("service", e.target.value)}>
            <option value="">Product / Service Interest</option>
            <option>Digital Signage Software</option>
            <option>Android Signage Player</option>
            <option>Commercial LED TV</option>
            <option>LED Active Display / Video Wall</option>
            <option>LED Interactive Panel</option>
            <option>LED Floor Stand Display</option>
            <option>LED Wall Mounted Display</option>
            <option>Token Display System</option>
            <option>Restaurant QR Ordering</option>
            <option>Other</option>
          </select>
          <fieldset className="rounded-xl border border-slate-200 px-4 py-3">
            <legend className="px-1 text-xs font-bold uppercase tracking-wide text-slate-500">Preferred contact method</legend>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {["Phone", "WhatsApp", "Email"].map((option) => (
                <label key={option} className="flex items-center gap-2">
                  <input type="radio" name="preferredContact" checked={form.preferredContact === option} onChange={() => update("preferredContact", option)} />
                  {option}
                </label>
              ))}
            </div>
          </fieldset>
          <textarea className="min-h-32 rounded-xl border border-slate-200 px-4 py-3" placeholder="Requirement" value={form.message} onChange={(e) => update("message", e.target.value)} minLength={10} maxLength={2000} required />
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} required />
            I agree to be contacted by RP Infotech about this enquiry via phone, WhatsApp or email.
          </label>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}
          {notice && <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">{notice}</div>}
          <button type="submit" disabled={submitting} className="rounded-xl bg-orange-500 px-5 py-4 text-center font-black text-white disabled:cursor-not-allowed disabled:opacity-70">
            {submitting ? "Sending..." : "Send Enquiry"}
          </button>
        </form>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
          <h2 className="text-2xl font-black text-slate-950">Before You Reach Out</h2>
          <div className="mt-6 grid gap-3">
            {contactFaqs.map((row) => (
              <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="cursor-pointer font-black text-slate-900">{row.question}</summary>
                <p className="mt-3 text-slate-600">{row.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </EnterpriseShell>
  );
}

export function AboutPage() {
  const { data } = useWebsiteContent();
  const settings = data.settings || {};
  const industries = publicItems(data.industries).slice(0, 8);
  useEnterpriseSeo("about", {
    metaTitle: "About RP Signage | RP Infotech Digital Signage & Display Solutions",
    metaDescription: "RP Signage is RP Infotech's LED display and digital signage platform - LED TVs, LED active display video walls, interactive panels, floor stand and wall mounted displays - serving Vasai, Virar, Mumbai, Thane and Maharashtra.",
  });
  const capabilities = [
    { title: "Software", description: "The RP Signage cloud platform for content scheduling, screen groups, playlists and remote monitoring." },
    { title: "Hardware", description: "Android signage players, commercial LED TVs, LED active display video walls, LED interactive panels, floor stand and wall mounted displays." },
    { title: "Installation", description: "Site survey, mounting, cabling, network setup and configuration by our installation team." },
    { title: "Support", description: "Ongoing support and Annual Maintenance Contract options for existing installations." },
  ];
  const whyChoose = [
    "One point of contact for software, hardware and installation instead of juggling multiple vendors",
    "A cloud platform built for dealers and multi-location businesses to manage screens centrally",
    "Local, on-site installation and support across Vasai, Virar, Nalasopara, Mumbai, Thane and Palghar",
    "Solutions scoped to the business - a single screen or a multi-location deployment",
  ];
  return (
    <EnterpriseShell content={data}>
      <PageHero
        title="About RP Signage"
        subtitle="RP Signage is RP Infotech's LED display and digital signage platform, built to help businesses manage screens, content and customer communication from one place."
        image="/placeholders/about-banner.svg"
      />

      <section className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
        <h2 className="text-3xl font-black text-slate-950">What RP Signage Provides</h2>
        <p className="mt-4 text-lg leading-8 text-slate-700">
          RP Signage brings together cloud digital signage software, Android signage players, commercial LED TVs, LED active display video walls, LED interactive panels, LED floor stand and wall mounted displays under one platform from RP Infotech. Instead of updating printed notices, posters or menus by hand, businesses can publish and schedule content to one screen or many from a single dashboard.
        </p>
        <p className="mt-4 text-lg leading-8 text-slate-700">
          We work with retail stores, restaurants and cafes, clinics and hospitals, corporate offices, housing societies, schools, hotels and salons - each with a different signage need, from promotional displays to token queues to digital menu boards.
        </p>
        <img src={resolveWebsiteImage("/placeholders/about-rp-signage.svg")} alt="RP Signage software, hardware and installation overview" className="mt-8 h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80" />
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <h2 className="text-3xl font-black text-slate-950">Problems We Help Solve</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {[
              "Manually updating printed notices, menus or posters across one or more locations",
              "Delayed communication when offers, schedules or announcements change",
              "Disconnected hardware and software from different vendors that are hard to support together",
              "Inconsistent branding and messaging across multiple screens or branches",
            ].map((point) => (
              <div key={point} className="flex items-start gap-3 rounded-xl bg-white p-5 shadow-sm">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                <span className="font-semibold text-slate-700">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <h2 className="text-3xl font-black text-slate-950">Software, Hardware, Installation and Support</h2>
        <p className="mt-3 text-slate-600">RP Infotech coordinates all four so businesses deal with one team, not four vendors.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((cap) => (
            <div key={cap.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-lg font-black text-slate-950">{cap.title}</div>
              <p className="mt-2 text-sm text-slate-600">{cap.description}</p>
            </div>
          ))}
        </div>
      </section>

      {industries.length > 0 && <CardGrid title="Industries We Serve" subtitle="Digital signage and display solutions built around each business type." items={industries} base="/industries" />}

      <section className="bg-[#07142f] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <h2 className="text-3xl font-black">Why Businesses Choose RP Signage</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {whyChoose.map((point) => (
              <div key={point} className="flex items-start gap-3 rounded-xl bg-white/5 p-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-400" />
                <span className="font-semibold text-slate-100">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
        <h2 className="text-3xl font-black text-slate-950">Our Approach</h2>
        <p className="mt-4 text-lg leading-8 text-slate-700">
          We approach every deployment the same way: understand the business and the space, recommend the right mix of software and hardware for the requirement and budget, install and configure it properly, and stay available for support and maintenance afterwards. The same platform scales from a single screen to a multi-location, multi-dealer rollout.
        </p>
        <h3 className="mt-8 text-xl font-black text-slate-950">Service Coverage</h3>
        <p className="mt-3 text-slate-700">We provide installation and support across Vasai, Virar, Nalasopara, Mumbai, Thane, Navi Mumbai, Palghar and the wider Maharashtra region.</p>
      </section>

      <Cta settings={settings} />
    </EnterpriseShell>
  );
}

export function RequestDemoPage() {
  const { data } = useWebsiteContent();
  const settings = data.settings || {};
  const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "", city: "", industry: "", screens: "", displayType: "", service: "", message: "", contactTime: "", consent: false, website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  useEnterpriseSeo("request-demo", { metaTitle: "Request a Demo | RP Signage", metaDescription: "Book a live demo of RP Signage digital signage software, Android players, displays and business automation solutions." });

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setNotice("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const digits = form.mobile.replace(/\D+/g, "");
    if (form.name.trim().length < 2) return setError("Please enter your name.");
    if (digits.length < 10 || digits.length > 15) return setError("Please enter a valid mobile number.");
    if (!form.consent) return setError("Please accept to be contacted about your demo request.");
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const messageLines = [
        form.message || "Requesting a live demo of RP Signage.",
        form.city ? `City: ${form.city}` : "",
        form.industry ? `Industry: ${form.industry}` : "",
        form.screens ? `Number of screens: ${form.screens}` : "",
        form.displayType ? `Display type: ${form.displayType}` : "",
        form.contactTime ? `Preferred contact time: ${form.contactTime}` : "",
      ].filter(Boolean);
      await api.post("/website-enquiries", {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        organization: form.company,
        service: form.service,
        message: messageLines.join("\n"),
        honeypot: form.website,
        pageUrl: window.location.href,
        source: "request_demo_page",
        deviceType: contactDeviceType(),
        referrer: document.referrer || "",
        visitorId: analyticsStorageId(window.localStorage, "rp_analytics_visitor_id", "visitor"),
        sessionId: analyticsStorageId(window.sessionStorage, "rp_analytics_session_id", "session"),
      });
      setNotice("Thanks - your demo request has been received. Our team will contact you shortly.");
      setForm({ name: "", company: "", mobile: "", email: "", city: "", industry: "", screens: "", displayType: "", service: "", message: "", contactTime: "", consent: false, website: "" });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail || err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EnterpriseShell content={data}>
      <PageHero title="Request a Live Demo" subtitle="See RP Signage running on real screens before you decide. Tell us about your business and we will set up a demo suited to your requirement." image="/placeholders/demo-banner.svg" />
      <section className="mx-auto grid max-w-[1000px] gap-6 px-4 py-12 lg:px-8">
        <form onSubmit={submit} className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2">
          <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => update("website", e.target.value)} name="website" />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Name" value={form.name} onChange={(e) => update("name", e.target.value)} maxLength={120} required />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Company name" value={form.company} onChange={(e) => update("company", e.target.value)} maxLength={180} />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Mobile number" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} inputMode="tel" maxLength={20} required />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} type="email" maxLength={180} />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} maxLength={100} />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Industry (e.g. Retail, Restaurant)" value={form.industry} onChange={(e) => update("industry", e.target.value)} maxLength={100} />
          <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Number of screens" value={form.screens} onChange={(e) => update("screens", e.target.value)} inputMode="numeric" maxLength={10} />
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3" value={form.displayType} onChange={(e) => update("displayType", e.target.value)}>
            <option value="">Display type</option>
            <option>Commercial LED TV</option>
            <option>LED Active Display / Video Wall</option>
            <option>LED Interactive Panel</option>
            <option>LED Floor Stand Display</option>
            <option>LED Wall Mounted Display</option>
            <option>Not sure yet</option>
          </select>
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3 sm:col-span-2" value={form.service} onChange={(e) => update("service", e.target.value)}>
            <option value="">Product interest</option>
            <option>Digital Signage Software</option>
            <option>Android Signage Player</option>
            <option>Commercial LED TV</option>
            <option>LED Active Display / Video Wall</option>
            <option>LED Interactive Panel</option>
            <option>LED Floor Stand Display</option>
            <option>LED Wall Mounted Display</option>
            <option>Token Display System</option>
            <option>Restaurant QR Ordering</option>
            <option>Other</option>
          </select>
          <textarea className="min-h-24 rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2" placeholder="Tell us about your requirement" value={form.message} onChange={(e) => update("message", e.target.value)} maxLength={2000} />
          <input className="rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2" placeholder="Preferred date or time to contact you (optional)" value={form.contactTime} onChange={(e) => update("contactTime", e.target.value)} maxLength={120} />
          <label className="flex items-start gap-2 text-sm text-slate-600 sm:col-span-2">
            <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
            I agree to be contacted by RP Infotech about this demo request.
          </label>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 sm:col-span-2">{error}</div>}
          {notice && <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 sm:col-span-2">{notice}</div>}
          <button type="submit" disabled={submitting} className="rounded-xl bg-orange-500 px-5 py-4 text-center font-black text-white disabled:cursor-not-allowed disabled:opacity-70 sm:col-span-2">
            {submitting ? "Sending..." : "Request Demo"}
          </button>
          <a href={whatsappUrl(settings, "Hello RP Infotech, I want to book a live demo of RP Signage.")} className="rounded-xl border border-green-500 px-5 py-4 text-center font-black text-green-700 sm:col-span-2">Request on WhatsApp instead</a>
        </form>
      </section>
    </EnterpriseShell>
  );
}

export function DealerProgramPage() {
  const { data } = useWebsiteContent();
  const settings = data.settings || {};
  const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "", city: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  useEnterpriseSeo("dealer-program", { metaTitle: "Become a Dealer | RP Signage Partner Program", metaDescription: "Partner with RP Infotech to sell and support RP Signage digital signage software, LED TVs, LED active displays and LED interactive panels." });

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setNotice("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const digits = form.mobile.replace(/\D+/g, "");
    if (form.name.trim().length < 2) return setError("Please enter your name.");
    if (digits.length < 10 || digits.length > 15) return setError("Please enter a valid mobile number.");
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      await api.post("/website-enquiries", {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        organization: form.company,
        service: "Dealer Program",
        message: [form.message || "Interested in the RP Infotech dealer program.", form.city ? `City: ${form.city}` : ""].filter(Boolean).join("\n"),
        honeypot: form.website,
        pageUrl: window.location.href,
        source: "dealer_program_page",
        deviceType: contactDeviceType(),
        referrer: document.referrer || "",
        visitorId: analyticsStorageId(window.localStorage, "rp_analytics_visitor_id", "visitor"),
        sessionId: analyticsStorageId(window.sessionStorage, "rp_analytics_session_id", "session"),
      });
      setNotice("Thanks for your interest. Our team will get in touch about the dealer program.");
      setForm({ name: "", company: "", mobile: "", email: "", city: "", message: "", website: "" });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail || err));
    } finally {
      setSubmitting(false);
    }
  };

  const opportunities = [
    { title: "Software and Hardware Portfolio", description: "Offer the RP Signage cloud platform alongside Android signage players, LED TVs, LED active display video walls, interactive panels, floor stands and wall mounted displays under one dealer account." },
    { title: "Dealer Panel", description: "Manage your own clients, screens and subscriptions through a dedicated dealer panel." },
    { title: "Installation and Service Opportunity", description: "Take on installation and AMC work for the clients you bring on, in addition to product sales." },
    { title: "Sales Support", description: "Get product information, demo access and guidance from the RP Infotech team as you sell." },
  ];

  const dealerFaqs = [
    { question: "Who can apply for the RP Infotech dealer program?", answer: "IT resellers, LED display and signage installers, and system integrators who already work with business customers are a good fit." },
    { question: "Is there a guaranteed territory or lead volume?", answer: "No. We do not offer guaranteed territory exclusivity, guaranteed profit or guaranteed leads. The dealer panel and product support are provided to help you sell and service RP Signage effectively." },
    { question: "Do dealers need to stock hardware?", answer: "This depends on the arrangement discussed with our team; some dealers order hardware per project rather than holding stock." },
  ];

  return (
    <EnterpriseShell content={data}>
      <PageHero title="Become an RP Infotech Dealer" subtitle="Partner with RP Infotech to sell and support digital signage software, LED TVs, LED active displays and LED interactive panels in your area." image="/placeholders/dealer-banner.svg" />

      <section className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <h2 className="text-3xl font-black text-slate-950">Dealer Program Overview</h2>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-700">
          The RP Infotech dealer program lets installers, IT resellers and system integrators offer RP Signage software and hardware to their own customers, backed by a dealer panel to manage clients and product support from our team.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {opportunities.map((item) => (
            <div key={item.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="font-black text-slate-950">{item.title}</div>
              <p className="mt-2 text-sm text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
          <h2 className="text-3xl font-black text-slate-950">Dealer Enquiries</h2>
          <form onSubmit={submit} className="mt-6 grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2">
            <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => update("website", e.target.value)} name="website" />
            <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Name" value={form.name} onChange={(e) => update("name", e.target.value)} maxLength={120} required />
            <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Company / firm name" value={form.company} onChange={(e) => update("company", e.target.value)} maxLength={180} />
            <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Mobile number" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} inputMode="tel" maxLength={20} required />
            <input className="rounded-xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} type="email" maxLength={180} />
            <input className="rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2" placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} maxLength={100} />
            <textarea className="min-h-24 rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2" placeholder="Tell us about your business" value={form.message} onChange={(e) => update("message", e.target.value)} maxLength={2000} />
            {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 sm:col-span-2">{error}</div>}
            {notice && <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700 sm:col-span-2">{notice}</div>}
            <button type="submit" disabled={submitting} className="rounded-xl bg-orange-500 px-5 py-4 text-center font-black text-white disabled:cursor-not-allowed disabled:opacity-70 sm:col-span-2">
              {submitting ? "Sending..." : "Send Dealer Enquiry"}
            </button>
            <a href={whatsappUrl(settings, "Hello RP Infotech, I am interested in the dealer program.")} className="rounded-xl border border-green-500 px-5 py-4 text-center font-black text-green-700 sm:col-span-2">Enquire on WhatsApp instead</a>
          </form>
        </div>
      </section>

      <Faq rows={dealerFaqs} />
    </EnterpriseShell>
  );
}

export function NotFoundPage() {
  const { data } = useWebsiteContent();
  useEnterpriseSeo("404", { metaTitle: "Page Not Found | RP Infotech", robots: "noindex, follow" });
  return (
    <EnterpriseShell content={data}>
      <section className="mx-auto max-w-[700px] px-4 py-24 text-center">
        <div className="text-sm font-black uppercase tracking-[0.2em] text-blue-700">404</div>
        <h1 className="mt-3 text-4xl font-black text-slate-950">Page not found</h1>
        <p className="mt-4 text-lg text-slate-600">The page you are looking for may have moved or no longer exists.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="rounded-xl bg-slate-950 px-5 py-3 font-black text-white">Go to Homepage</Link>
          <Link to="/products" className="rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-950">Browse Products</Link>
          <Link to="/contact" className="rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-950">Contact Us</Link>
        </div>
      </section>
    </EnterpriseShell>
  );
}

const PRICING_PLANS = [
  {
    name: "Agency / Standard Plan",
    tagline: "Cloud content push for a single screen or a handful of screens.",
    audience: "Individual businesses and dealers/agencies managing a few client screens.",
    features: [
      "Push content, images and playlists to the screen from the cloud dashboard",
      "Ready-made templates and scheduling",
      "Works with Android TV / Android signage players",
      "Remote screen monitoring and support",
    ],
    highlight: false,
  },
  {
    name: "Solution-Based Plan",
    tagline: "Everything in Standard, plus a ready-made module for your industry.",
    audience: "Salons, clinics, hospitals, schools and restaurants that want a setup built for their exact workflow.",
    features: [
      "Live token/queue display zone for salons, clinics and hospitals",
      "Timetable and notice modules for schools",
      "QR table ordering with kitchen and waiter routing for restaurants",
      "Multi-zone layout: promotions, ticker and industry module together on one screen",
    ],
    highlight: true,
  },
  {
    name: "Corporate Bulk Plan",
    tagline: "Everything in Standard, sized for many screens across locations.",
    audience: "Corporate offices, housing societies, retail chains and multi-branch businesses.",
    features: [
      "Bulk device onboarding and setup",
      "Centralized dashboard across multiple locations",
      "Dealer / multi-client account management",
      "Priority installation and support",
    ],
    highlight: false,
  },
];

export function PricingPage() {
  const { data } = useWebsiteContent();
  useEnterpriseSeo("pricing", { metaTitle: "Pricing Plans | RP Infotech", metaDescription: "Agency, solution-based and corporate bulk plans for RP Signage digital signage software and LED displays. Contact us for a custom quote." });
  const settings = data?.settings || {};
  const inquiry = whatsappUrl(settings, "Hello RP Infotech, I would like pricing details for RP Signage plans.");
  return (
    <EnterpriseShell content={data}>
      <PageHero title="Pricing Plans" subtitle="Flexible plans for software, hardware and service deployments - sized for a single screen, an industry-specific setup, or a multi-location rollout." image="/placeholders/pricing-banner.svg" />

      <section className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-black text-slate-950">Choose the Plan That Fits Your Business</h2>
          <p className="mt-3 text-slate-600">Pricing depends on the number of screens, features and installation location, so we don't publish fixed prices - contact our team and we'll put together a quote for your setup.</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {PRICING_PLANS.map((plan) => (
            <div key={plan.name} className={`flex flex-col rounded-2xl border p-6 shadow-sm ${plan.highlight ? "border-orange-300 bg-orange-50/40 ring-2 ring-orange-200" : "border-slate-200 bg-white"}`}>
              {plan.highlight && <span className="mb-3 inline-flex w-max rounded-full bg-orange-500 px-3 py-1 text-xs font-black text-white">Most Popular</span>}
              <h3 className="text-xl font-black text-slate-950">{plan.name}</h3>
              <p className="mt-2 text-sm font-semibold text-slate-600">{plan.tagline}</p>
              <p className="mt-3 text-sm text-slate-500">{plan.audience}</p>
              <div className="mt-5 grid gap-2.5">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" /> {feature}
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-slate-50 px-4 py-3 text-center text-sm font-black text-slate-700">Contact us for pricing</div>
              <div className="mt-4 grid gap-2">
                <Link to="/contact#demo" className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-5 py-3 text-center font-black text-white">Get a Custom Quote</Link>
                <a href={inquiry} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-xl border border-green-600 px-5 py-3 text-center font-black text-green-700">WhatsApp Enquiry</a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
          Every plan includes the RP Signage cloud app - and it's free for the first year with any LED display purchase. Final pricing is confirmed after a quick call about your screens, locations and features needed.
        </div>
      </section>
    </EnterpriseShell>
  );
}

export function SimpleWebsitePage({ pageKey = "page", title = "RP Infotech", subtitle = "" }) {
  const { data } = useWebsiteContent();
  useEnterpriseSeo(pageKey, pageKey === "privacy-policy" ? {
    metaTitle: "Privacy Policy for RP Society & RP Signage | RP Infotech",
    metaDescription: "Privacy Policy for the RP Society Android app and RP Signage website and services, including visitor management, billing, calls and notifications.",
  } : { metaTitle: `${title} | RP Infotech` });
  const legalContent = legalPageContent[pageKey];
  return (
    <EnterpriseShell content={data}>
      <PageHero title={title} subtitle={subtitle || data.settings?.footer} />
      <section className="mx-auto max-w-[1000px] px-4 py-12 text-base leading-7 text-slate-700 lg:px-8">
        {legalContent ? (
          <div className="space-y-8">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-5">
              <p className="font-semibold text-slate-950">{legalContent.intro}</p>
              <p className="mt-2 text-sm text-slate-500">Last updated: {pageKey === "privacy-policy" ? "6 September 2026" : "29 June 2026"}</p>
            </div>
            {legalContent.sections.map((section) => (
              <div key={section.title}>
                <h2 className="text-2xl font-black text-slate-950">{section.title}</h2>
                <div className="mt-3 space-y-3">
                  {section.body.map((line) => <p key={line}>{line}</p>)}
                </div>
              </div>
            ))}
            <div className="rounded-sm border border-slate-200 p-5">
              <h2 className="text-xl font-black text-slate-950">Contact</h2>
              <p className="mt-2">{pageKey === "privacy-policy"
                ? "For privacy questions, corrections or deletion requests concerning RP Society, contact RP Infotech at support@rp-infotech.com. For general RP Signage enquiries, contact info@rpsignage.com or +91 93230 37254."
                : "For questions about this page, contact RP Infotech at info@rpsignage.com or +91 93230 37254."}</p>
            </div>
          </div>
        ) : (
          <p>{subtitle || "This page content can be managed from the Admin Website CMS."}</p>
        )}
      </section>
    </EnterpriseShell>
  );
}

const legalPageContent = {
  "privacy-policy": {
    intro: "This Privacy Policy explains how RP Infotech collects, uses, stores, shares, and protects information when people use the RP Society Android app, the RP Signage website, RP Signage software, and related services.",
    sections: [
      {
        title: "1. Introduction",
        body: [
          "RP Infotech provides RP Society, a housing society visitor-entry and resident-management app, as well as the RP Signage website, digital signage software, and related business services. This policy applies to information processed through these products and services.",
          "The information available to a user and the information processed about that user depend on their role, such as resident, society staff, security staff, administrator, customer, dealer, or website visitor.",
        ],
      },
      {
        title: "2. Information We Collect",
        body: [
          "Depending on the service used, we may collect account and contact information, visitor and society records, billing and payment-reference information, support communications, device and technical information, and activity required to provide and secure the service.",
          "We may collect business contact details such as name, phone number, email address, company name, billing details, device information, support requests, and communication preferences for RP Signage and related services.",
          "For RP Signage software, we may process device IDs, playlist data, media metadata, campaign records, lead records, delivery status, and account activity required to operate the service.",
        ],
      },
      {
        title: "3. Resident Account and Login Information",
        body: [
          "RP Society residents sign in using a society code, a mobile number or email address, and a password. We process these details to authenticate the resident, connect the resident to the correct society and flat, maintain the login session, and prevent unauthorised access.",
          "RP Society does not use Firebase Authentication. If a user chooses the app's remember-password option, the login details are stored on that user's device using the app's protected local credential storage. Users should keep their device and password secure.",
        ],
      },
      {
        title: "4. Visitor Information",
        body: [
          "RP Society may process a visitor's name, mobile number, purpose of visit, society wing or flat being visited, entry time, gate or security details, and approval status. This information is used to register the visit, contact or notify the relevant resident, record approval or rejection, support gate operations, and maintain society security records.",
          "The current RP Society Android app does not collect visitor photographs or vehicle numbers. If these capabilities are introduced later, this policy and the relevant app disclosures will be updated before they are used.",
        ],
      },
      {
        title: "5. Society and Flat Information",
        body: [
          "RP Society processes society membership and unit information such as the society name or code, resident name, mobile number or email address, wing, flat or unit number, resident role, notices, visitor requests, bill status, and other records made available by the resident's society.",
          "The participating housing society and its authorised administrators determine and manage many of these records. Access is limited according to the user's role and the functions provided by the service.",
        ],
      },
      {
        title: "6. Billing and Payment-Reference Information",
        body: [
          "Residents can view and download their latest society bill. The app may display information such as the bill number, billing period, charges, outstanding balance, payment status, and related society or unit details.",
          "A resident may enter an amount paid and a UTR or transaction-reference number and submit it to the society for verification. RP Society records this information as a payment claim or reference; the app itself does not process the underlying bank, card, or UPI payment.",
          "RP Society does not collect bank-account details, payment-card details, UPI PINs, or other payment credentials. Users should never enter a PIN, password, one-time password, or complete card or bank credentials into the UTR/reference field.",
        ],
      },
      {
        title: "7. Camera and Microphone Usage",
        body: [
          "RP Society requests camera access only to provide live video calling. It requests microphone access only to provide live voice and video calling. These permissions are not used by the current app to take visitor photographs, scan QR codes, or make audio or video recordings.",
          "Android asks the user for the relevant permission. If permission is refused, the related audio or video function may be unavailable, while other app functions may continue to work.",
        ],
      },
      {
        title: "8. WebRTC Voice and Video Calling",
        body: [
          "Live voice and video calls in RP Society use native WebRTC technology. The RP Society server handles call setup and signalling, and network infrastructure such as STUN or TURN services may process technical connection information needed to establish and maintain the call.",
          "Live audio and video are transmitted for the call and are not presented as recorded content by the current app. Network details and limited call-status information may be processed to connect, operate, troubleshoot, and secure the calling feature.",
        ],
      },
      {
        title: "9. Push Notifications and Firebase Cloud Messaging",
        body: [
          "RP Society uses Firebase Cloud Messaging, provided by Google, only to deliver push notifications and incoming-call notifications. A device notification token and related technical information are processed so notifications can reach the correct device.",
          "The current RP Society app does not use Firebase Authentication, Firebase Database, Firebase Analytics, or Firebase Crashlytics.",
        ],
      },
      {
        title: "10. How We Use Information",
        body: [
          "We use information to authenticate users; provide visitor entry and approval, resident management, bills, payment-reference submission, notices, calls and notifications; operate RP Signage and related business services; respond to support requests; maintain security; diagnose faults; and meet applicable accounting or legal obligations.",
          "For RP Signage services, we may also use information to provide digital signage, CRM, WhatsApp communication, booking, catalogue, billing, support, and related business functions. Contact details may be used to respond to enquiries and send necessary service communications.",
        ],
      },
      {
        title: "11. Sharing with Societies, Service Providers and Others",
        body: [
          "We do not sell personal information. Information may be made available to the relevant housing society and its authorised administrators or security personnel where needed to provide RP Society functions.",
          "We may share or allow limited processing by service providers that support hosting, storage, network connectivity, WebRTC call connectivity, Firebase Cloud Messaging, communications, security, payment-related services used by RP Signage, analytics used by other RP Signage services, and customer support. These providers process information for the services they supply and are subject to their own terms and privacy practices.",
          "Information may also be disclosed when reasonably necessary to comply with applicable law or legal process, investigate misuse, protect users and society premises, or protect the rights and safety of RP Infotech, its customers, or other people.",
        ],
      },
      {
        title: "12. Data Security",
        body: [
          "We use reasonable administrative, technical, and organisational safeguards designed to protect information against unauthorised access, loss, misuse, alteration, or disclosure. These include access controls and protected communications where supported by the service.",
          "No internet transmission or storage system can be guaranteed to be completely secure. Users should protect their passwords and devices and promptly report suspected unauthorised access.",
        ],
      },
      {
        title: "13. Data Retention",
        body: [
          "We retain information for as long as reasonably needed to provide and secure the relevant service, maintain society visitor and accounting records, resolve disputes, respond to support matters, enforce agreements, and meet applicable legal or operational requirements.",
          "Retention periods can vary according to the type of record and the requirements of the participating society. When information is no longer reasonably required, it may be deleted or anonymised, subject to backup cycles and legitimate record-keeping obligations.",
        ],
      },
      {
        title: "14. User Rights, Corrections and Deletion Requests",
        body: [
          "Users may ask to access, correct, or delete their personal information by contacting their society administrator or emailing RP Infotech at support@rp-infotech.com. Please identify the society and flat or account concerned and provide enough information for us to verify and process the request safely.",
          "The current RP Society app does not provide an in-app account-deletion feature. A request may be limited where retention is reasonably necessary for society security, accounting, dispute resolution, fraud prevention, enforcement, or applicable legal obligations. We will explain any applicable limitation when responding to a request.",
        ],
      },
      {
        title: "15. Children and Minors",
        body: [
          "RP Society and RP Signage are intended for housing society, resident, staff, security, and business use and are not directed to children. Children should not create accounts or submit personal information through the services without the involvement of a parent, guardian, or authorised society representative.",
          "If you believe that a child has provided personal information inappropriately, contact support@rp-infotech.com so the matter can be reviewed.",
        ],
      },
      {
        title: "16. Third-Party Services",
        body: [
          "RP Society relies on third-party technology where necessary, including Google Firebase Cloud Messaging for notifications, Android and Google Play platform services, hosting and network providers, and WebRTC-related connectivity services. Their handling of information is governed by their applicable privacy policies and terms.",
          "RP Signage may also integrate with services such as the official WhatsApp Cloud API and other customer-selected business services. WhatsApp marketing messages are sent only where opt-in or another valid basis is available. If a recipient requests an applicable opt-out, we update the communication preference.",
        ],
      },
      {
        title: "17. Changes to This Privacy Policy",
        body: [
          "We may update this policy when our products, data practices, service providers, or applicable requirements change. The revised policy will be posted at https://rpsignage.com/privacy-policy with an updated revision date.",
          "Material changes may also be communicated through the app, website, email, or the participating society where appropriate.",
        ],
      },
      {
        title: "18. Contact Information",
        body: [
          "RP Society is provided by RP Infotech. Website: https://rpsignage.com/. For privacy questions, data corrections, or deletion requests, email support@rp-infotech.com.",
          "For general RP Signage enquiries, you may also use the contact information published on the RP Signage website.",
        ],
      },
    ],
  },
  "terms-and-conditions": {
    intro: "These Terms and Conditions govern the use of RP Infotech websites, RP Signage software, digital signage services, WhatsApp integrations, and related business solutions.",
    sections: [
      {
        title: "Use Of Services",
        body: [
          "Users must provide accurate account, billing, business, and contact information and must use the services only for lawful business purposes.",
          "Users are responsible for the content, media, messages, playlists, contacts, and campaigns uploaded or sent through RP Signage.",
        ],
      },
      {
        title: "WhatsApp And Communication Compliance",
        body: [
          "Users must send WhatsApp messages only to recipients who have provided valid consent where required. Promotional messages must use approved Meta WhatsApp templates.",
          "Users must not use RP Signage for spam, unlawful scraping, misleading communication, prohibited products, or messages that violate Meta, WhatsApp, telecom, or local regulations.",
        ],
      },
      {
        title: "Payments And Subscriptions",
        body: [
          "Fees, subscription plans, hardware charges, installation charges, and service terms are communicated at the time of purchase or quotation.",
          "Access to paid services may be limited or suspended if invoices are overdue or if services are misused.",
        ],
      },
      {
        title: "Service Availability",
        body: [
          "We work to keep services available and reliable, but availability may depend on hosting providers, internet connectivity, device condition, third-party APIs, WhatsApp Cloud API, and maintenance work.",
          "RP Infotech is not responsible for interruptions caused by third-party platforms, customer networks, device misuse, or unauthorised changes.",
        ],
      },
      {
        title: "Intellectual Property",
        body: [
          "RP Signage software, designs, workflows, documentation, and related materials are owned by RP Infotech or licensed providers.",
          "Customers retain ownership of their uploaded business content but grant RP Infotech the limited right to process it for providing the service.",
        ],
      },
      {
        title: "Limitation Of Liability",
        body: [
          "To the maximum extent allowed by law, RP Infotech is not liable for indirect losses, loss of profit, lost business, missed campaigns, third-party API failures, or unauthorised customer use.",
          "Any liability is limited to the amount paid for the relevant service period, unless a different written agreement applies.",
        ],
      },
    ],
  },
  "refund-policy": {
    intro: "This Refund Policy explains how refunds, cancellations, and service adjustments are handled for RP Infotech software subscriptions, hardware purchases, and installation services.",
    sections: [
      {
        title: "Software Subscriptions",
        body: [
          "Subscription fees for RP Signage software are billed as agreed at the time of purchase. Refund eligibility for unused subscription periods is assessed case by case and should be raised with your RP Infotech contact or dealer.",
          "Downgrades, upgrades, or plan changes may affect billing for the current cycle; please confirm the impact with our team before changing your plan.",
        ],
      },
      {
        title: "Hardware Purchases",
        body: [
          "Hardware such as signage players, displays, LED video walls, and cameras is covered by the applicable brand or RP Infotech warranty terms shared at the time of sale.",
          "Requests for return, replacement, or refund of hardware should be raised promptly with the invoicing dealer or RP Infotech, along with proof of purchase.",
        ],
      },
      {
        title: "Installation And Service Charges",
        body: [
          "Installation and service charges reflect work already scheduled or completed and are generally non-refundable once the visit or work has taken place.",
          "If a scheduled installation needs to be cancelled or rescheduled, please inform RP Infotech or your dealer as early as possible.",
        ],
      },
      {
        title: "How To Request A Refund Or Cancellation",
        body: [
          "Contact RP Infotech using the details on the Contact Us page, or reach out to your dealer, with your invoice or order reference and the reason for the request.",
          "Refund timelines and methods depend on the original payment method and will be communicated once a request is reviewed.",
        ],
      },
    ],
  },
  "cookie-policy": {
    intro: "This Cookie Policy explains how RP Infotech websites use cookies and similar technologies when you visit rpsignage.com and related pages.",
    sections: [
      {
        title: "What Cookies We Use",
        body: [
          "We use essential cookies required for the website and customer/dealer/admin login sessions to function correctly.",
          "We may use analytics-related storage to understand website usage, such as which pages are visited, in order to improve the site.",
        ],
      },
      {
        title: "Managing Cookies",
        body: [
          "Most browsers let you view, delete, or block cookies through their settings. Blocking essential cookies may affect your ability to log in or use certain features of the website.",
        ],
      },
      {
        title: "Third-Party Services",
        body: [
          "Some pages may load third-party services (for example, reCAPTCHA for spam protection) that can set their own cookies according to their respective policies.",
        ],
      },
    ],
  },
};

export function BlogPage() {
  const { data } = useWebsiteContent();
  return <IndexPage content={data} title="Blog" subtitle="Articles, product notes and business display ideas." rows={data.blogs} base="/blog" />;
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const { data } = useWebsiteContent();
  return <DetailPage content={data} item={findBySlug(data.blogs, slug)} type="article" base="/blog" />;
}

export function DownloadsPage() {
  const { data } = useWebsiteContent();
  return <IndexPage content={data} title="Downloads" subtitle="Brochures, datasheets and catalogs." rows={data.downloads} base="/downloads" />;
}

const FAQ_CATEGORY_ORDER = [
  "General", "Software", "Hardware", "Installation", "Internet & Connectivity", "Offline Playback",
  "Content Formats", "Screen Orientation", "Multi-Location", "Pricing & Quotation", "Support", "Dealer Program", "Data Privacy",
];

export function FaqPage() {
  const { data } = useWebsiteContent();
  const rows = publicItems(data.faqs);
  const faqSchema = rows.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: rows.filter((row) => row.question && row.answer).map((row) => ({
      "@type": "Question",
      name: row.question,
      acceptedAnswer: { "@type": "Answer", text: row.answer },
    })),
  } : null;
  useEnterpriseSeo("faq", {
    metaTitle: "FAQ | RP Signage LED Display & Digital Signage Questions",
    metaDescription: "Answers about RP Signage software, hardware, installation, internet requirements, offline playback, pricing and support.",
    ...(faqSchema ? { schemaJson: JSON.stringify(faqSchema) } : {}),
  });
  const grouped = useMemo(() => {
    const byCategory = new Map();
    rows.forEach((row) => {
      const category = row.category?.trim() || "General";
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(row);
    });
    const orderedKeys = [
      ...FAQ_CATEGORY_ORDER.filter((key) => byCategory.has(key)),
      ...[...byCategory.keys()].filter((key) => !FAQ_CATEGORY_ORDER.includes(key)),
    ];
    return orderedKeys.map((category) => ({ category, items: byCategory.get(category) }));
  }, [rows]);

  return (
    <EnterpriseShell content={data}>
      <PageHero title="Frequently Asked Questions" subtitle="Answers about RP Signage software, hardware, installation, connectivity, pricing, support and the dealer program." image="/placeholders/faq-banner.svg" />
      <section className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
        {grouped.length === 0 && <p className="text-slate-500">FAQs will appear here once added from the admin panel.</p>}
        <div className="grid gap-10">
          {grouped.map(({ category, items }) => (
            <div key={category}>
              <h2 className="text-2xl font-black text-slate-950">{category}</h2>
              <div className="mt-4 grid gap-3">
                {items.map((row) => (
                  <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <summary className="cursor-pointer font-black">{row.question}</summary>
                    <p className="mt-3 text-slate-600">{row.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Cta settings={data.settings} />
    </EnterpriseShell>
  );
}

function HeroMockup({ image }) {
  return (
    <div className="relative min-h-[360px]">
      <div className="absolute right-0 top-0 h-64 w-[78%] rounded-3xl border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur">
        <img src={resolveWebsiteImage(image || "/banner.png")} alt="RP Signage dashboard and display solutions" className="h-full w-full rounded-2xl object-cover opacity-90" />
      </div>
      <div className="absolute bottom-8 left-0 w-[58%] rounded-2xl border border-white/10 bg-white p-4 text-[#07142f] shadow-2xl">
        <div className="mb-3 grid grid-cols-4 gap-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-12 rounded-xl bg-blue-100" />)}</div>
        <div className="h-28 rounded-xl bg-gradient-to-br from-blue-600 to-orange-400" />
      </div>
      <div className="absolute bottom-0 right-10 rounded-2xl bg-[#101b3c] p-5 text-center shadow-2xl">
        <div className="text-sm text-slate-300">TOKEN</div>
        <div className="text-4xl font-black">A 001</div>
        <div className="mt-2 text-sm text-slate-300">COUNTER 2</div>
      </div>
    </div>
  );
}

function PageHero({ title, subtitle, image }) {
  return (
    <section className="relative w-full overflow-hidden bg-[#07142f] text-white">
      <img src={resolveWebsiteImage(image || "/banner.png")} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      {/* Uniform tint keeps the whole photo visible (no lopsided dark wash on one side); the text card below guarantees legibility no matter what the photo looks like underneath. Swapping in a real photo later needs no re-tuning. */}
      <div className="absolute inset-0 bg-[#07142f]/45" />
      <div className="relative mx-auto flex min-h-[360px] max-w-[1400px] items-center px-4 py-16 md:min-h-[440px] lg:px-8">
        <div className="max-w-2xl rounded-2xl bg-[#07142f]/80 p-6 shadow-xl backdrop-blur-sm sm:p-8">
          <h1 className="text-4xl font-black lg:text-5xl">{title}</h1>
          <p className="mt-4 text-lg text-slate-200">{subtitle}</p>
        </div>
      </div>
    </section>
  );
}

function Stats({ stats = [] }) {
  return <section className="bg-[#061327] text-white"><div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-4 px-4 py-5 lg:grid-cols-4 lg:px-8">{(stats || []).map((s) => <div key={s.label} className="text-center"><div className="text-2xl font-black">{s.value}</div><div className="text-sm text-slate-300">{s.label}</div></div>)}</div></section>;
}

function ProductStrip({ products }) {
  return <section className="mx-auto max-w-[1400px] px-4 py-10 lg:px-8"><h2 className="text-center text-3xl font-black">What We Sell</h2><div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">{products.map((p, i) => { const Icon = iconMap[i % iconMap.length]; return <Link key={p.slug} to={`/products/${p.slug}`} className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-xl"><Icon className="mx-auto h-10 w-10 text-blue-600" /><h3 className="mt-4 font-black">{p.title}</h3><p className="mt-2 text-sm text-slate-600">{p.summary}</p><span className="mt-4 inline-flex text-sm font-black text-blue-700">View More -></span></Link>; })}</div></section>;
}

function SplitShowcase({ industries, products }) {
  return <section className="mx-auto grid max-w-[1400px] gap-5 px-4 pb-10 lg:grid-cols-3 lg:px-8"><Panel title="Software + Hardware + Service" items={["Advanced cloud based software platform", "Wide range of signage players and devices", "Commercial LED TVs and LED Active Display video walls", "LED interactive panels, floor stands and wall mounts", "Professional installation and after-sales support"]} /><Panel title="Industries We Serve" items={industries.map((i) => i.title)} /><Panel title="Our LED Display Range" items={products.slice(1, 7).map((i) => i.title)} /></section>;
}

function Panel({ title, items }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"><h2 className="text-2xl font-black">{title}</h2><div className="mt-5 grid gap-3">{items.map((item) => <div key={item} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 text-blue-600" />{item}</div>)}</div></div>;
}

function CardGrid({ title, subtitle, items, base }) {
  return <section className="mx-auto max-w-[1400px] px-4 py-10 lg:px-8"><h2 className="text-3xl font-black">{title}</h2><p className="mt-2 text-slate-600">{subtitle}</p><div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">{publicItems(items).map((item) => <InfoCard key={item.slug} item={item} base={base} />)}</div></section>;
}

function InfoCard({ item, base }) {
  return <Link to={`${base}/${item.slug}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"><img src={resolveWebsiteImage(item.image)} alt={item.title} className="h-36 w-full rounded-xl object-cover" /><h3 className="mt-4 text-xl font-black">{item.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{item.summary}</p></Link>;
}

function productHighlights(item) {
  const fromKeyFeatures = (item?.keyFeatures || []).map((row) => (typeof row === "string" ? row : row?.title)).filter(Boolean);
  const source = fromKeyFeatures.length ? fromKeyFeatures : (item?.features || []);
  return source.filter(Boolean).slice(0, 5);
}

function availabilityBadgeClasses(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("stock")) return "bg-green-100 text-green-700";
  if (value.includes("subscription")) return "bg-blue-100 text-blue-700";
  if (value.includes("project") || value.includes("quotation")) return "bg-orange-100 text-orange-700";
  return "bg-slate-100 text-slate-600";
}

function ProductCard({ item, settings }) {
  const navigate = useNavigate();
  const isCategoryCard = item?.isCategoryOverview || sameProductCategoryName(item, item.category);
  const detailLink = isCategoryCard ? `/products?category=${encodeURIComponent(item.category || "")}` : `/products/${item.slug}`;
  const highlights = productHighlights(item);
  const badges = [item?.availabilityStatus, ...(item?.badges || [])].filter(Boolean);
  const goToDetail = () => navigate(detailLink);
  return (
    <article
      role="link"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          goToDetail();
        }
      }}
      className="group flex h-full cursor-pointer flex-col overflow-hidden border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        <img src={resolveWebsiteImage(item.image)} alt={item.title ? `${item.title} - RP Infotech` : "RP Infotech product"} className="h-full w-full object-contain p-4" loading="lazy" width="600" height="450" />
        {!isCategoryCard && badges.length > 0 && (
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {badges.slice(0, 2).map((badge) => (
              <span key={badge} className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${availabilityBadgeClasses(badge)}`}>{badge}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">{isCategoryCard ? (item.category || item.title || "Category") : (item.category || "Product")}</div>
        <h2 className="mt-2 text-xl font-black tracking-tight text-slate-950">{item.title}</h2>
        <div className="mt-1 text-sm font-semibold text-slate-500">{item.modelNumber || item.priceText || ""}</div>
        <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{item.summary}</p>
        {highlights.length > 0 && (
          <ul className="mt-4 grid gap-1.5 text-xs">
            {highlights.map((highlight) => (
              <li key={`${item.slug}-${highlight}`} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
                <span className="font-semibold">{highlight}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto grid gap-2 pt-5 sm:grid-cols-2">
          <Link to={detailLink} onClick={(event) => event.stopPropagation()} className="flex min-h-12 items-center justify-center rounded-sm bg-slate-950 px-4 py-3 text-center text-sm font-black text-white">{isCategoryCard ? "View Products" : "View Details"}</Link>
          <a href={whatsappUrl(settings, `Hello RP Infotech, I am interested in the following product:\n\nProduct: ${item.title}\nModel: ${item.modelNumber || "N/A"}\n\nPlease share pricing and availability.`)} onClick={(event) => event.stopPropagation()} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center rounded-sm border border-green-600 px-4 py-3 text-center text-sm font-black text-green-700">
            WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
}

function IndexPage({ content, title, subtitle, rows, base, image }) {
  return <EnterpriseShell content={content}><PageHero title={title} subtitle={subtitle} image={image} /><section className="mx-auto grid max-w-[1400px] gap-5 px-4 py-10 md:grid-cols-2 xl:grid-cols-3 lg:px-8">{publicItems(rows).map((item) => <InfoCard key={item.slug || item.title} item={item} base={base} />)}</section></EnterpriseShell>;
}

function DetailPage({ content, item, type, base, related = [] }) {
  if (!item) return <EnterpriseShell content={content}><PageHero title="Page not found" subtitle="This item is not available." /></EnterpriseShell>;
  const applications = item.applications || [];
  const faqs = (item.faqs || []).filter((row) => row && row.question);
  const settings = content?.settings || {};
  const inquiry = whatsappUrl(settings, `Hello RP Infotech, I would like to know more about ${item.title}.`);
  return (
    <EnterpriseShell content={content}>
      <PageHero title={item.title} subtitle={item.summary} image={item.image} />
      <section className="mx-auto grid max-w-[1400px] items-start gap-8 px-4 py-12 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div>
          <h2 className="text-3xl font-black">Overview</h2>
          <p className="mt-4 text-lg leading-8 text-slate-700">{item.description || item.summary}</p>
          <div className="mt-6 grid gap-3">{(item.features || item.benefits || []).map((feature) => <div key={feature} className="flex gap-3 rounded-xl bg-slate-50 p-4"><CheckCircle2 className="h-5 w-5 text-blue-600" /><span className="font-semibold">{feature}</span></div>)}</div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-[#07142f] p-6 text-white shadow-lg">
          <h3 className="text-2xl font-black">Need this {type}?</h3>
          <p className="mt-3 text-sm text-slate-300">Talk to RP Infotech for pricing, installation and a live demo.</p>
          <div className="mt-6 grid gap-3">
            <Link to="/contact" className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-5 py-3 text-center font-black">Book Demo</Link>
            <a href={inquiry} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-xl bg-green-600 px-5 py-3 text-center font-black">WhatsApp Enquiry</a>
            {type === "product" ? <BrochureButton product={item} /> : null}
          </div>
          <div className="mt-6 grid gap-2 border-t border-white/10 pt-5 text-sm text-slate-300">
            <a className="flex items-center gap-2" href={`tel:${String(settings.phone || "").replace(/\s/g, "")}`}><Phone className="h-4 w-4 text-orange-400" />{settings.phone}</a>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 flex-shrink-0 text-orange-400" />{settings.address}</div>
          </div>
        </div>
      </section>

      {applications.length > 0 && (
        <section className="bg-slate-50">
          <div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
            <h2 className="text-2xl font-black text-slate-950">How It's Used</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {applications.map((app) => (
                <div key={app.title} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="font-black text-slate-900">{app.title}</div>
                  <p className="mt-1 text-sm text-slate-600">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8">
          <h2 className="text-2xl font-black text-slate-950">Frequently Asked Questions</h2>
          <div className="mt-4 grid gap-3">
            {faqs.map((row) => (
              <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="cursor-pointer font-black">{row.question}</summary>
                <p className="mt-3 text-slate-600">{row.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {related?.length ? <CardGrid title="Related Products" subtitle="Recommended products for this page." items={related.slice(0, 3)} base="/products" /> : null}
    </EnterpriseShell>
  );
}

function productJsonLd(item, data) {
  if (!item) return null;
  const base = typeof window !== "undefined" ? window.location.origin : "https://rpsignage.com";
  const url = `${base}/products/${item.slug}`;
  const graph = [];
  graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: base },
      { "@type": "ListItem", position: 2, name: "Products", item: `${base}/products` },
      { "@type": "ListItem", position: 3, name: item.title, item: url },
    ],
  });
  graph.push({
    "@type": "Product",
    name: item.title,
    image: resolveWebsiteImage(item.image),
    description: item.summary || item.description || "",
    brand: { "@type": "Brand", name: "RP Infotech" },
    url,
  });
  const faqRows = (item.faqs || []).filter((row) => row && row.question && row.answer);
  if (faqRows.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqRows.map((row) => ({
        "@type": "Question",
        name: row.question,
        acceptedAnswer: { "@type": "Answer", text: row.answer },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

function SpecGroups({ groups = [], legacy = [] }) {
  if (groups.length) {
    return (
      <div className="grid gap-6">
        {groups.map((group) => (
          <div key={group.group}>
            <h3 className="text-sm font-black uppercase tracking-wide text-blue-700">{group.group}</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {(group.items || []).map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                  <span className="text-sm font-bold text-slate-500">{item.label}</span>
                  <span className={item.value ? "text-right text-sm font-black text-slate-800" : "text-right text-sm font-black italic text-slate-400"}>{item.value || "Pending verification"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!legacy.length) return <p className="text-slate-500">Technical information pending verification.</p>;
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {legacy.map((row) => {
        const text = String(row || "");
        if (text.includes(":")) {
          const parts = text.split(":");
          const label = parts.shift();
          return <Info key={text} label={label.trim()} value={parts.join(":").trim()} />;
        }
        return (
          <div key={text} className="flex items-center gap-2 border border-slate-100 bg-slate-50 p-3 text-sm font-semibold text-slate-700">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-blue-600" /> {text}
          </div>
        );
      })}
    </div>
  );
}

function VariantsTable({ columns = [], rows = [] }) {
  if (!rows.length) {
    return <p className="text-slate-500">Model-wise comparison will appear here once variants are added. Contact us for the current model line-up.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="bg-slate-950 text-white">
            {columns.map((col) => <th key={col} className="whitespace-nowrap px-3 py-3 text-left font-black">{col}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.Model || row.model || index} className={index % 2 ? "bg-slate-50" : "bg-white"}>
              {columns.map((col) => <td key={col} className="whitespace-nowrap border-b border-slate-100 px-3 py-3 font-semibold text-slate-700">{row[col] ?? "-"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductBreadcrumb({ product }) {
  return (
    <nav aria-label="Breadcrumb" className="mx-auto max-w-[1400px] px-4 pt-4 text-xs font-bold text-slate-500 lg:px-8">
      <ol className="flex flex-wrap items-center gap-1.5 [&>li+li]:before:mr-1.5 [&>li+li]:before:content-['/']">
        <li><Link to="/" className="hover:text-blue-700">Home</Link></li>
        <li><Link to="/products" className="hover:text-blue-700">Products</Link></li>
        {product.category ? (
          <li><Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-blue-700">{product.category}</Link></li>
        ) : null}
        <li className="text-slate-800" aria-current="page">{product.title}</li>
      </ol>
    </nav>
  );
}

function WebsiteProductDetail({ content, product, related = [] }) {
  const [activeImage, setActiveImage] = useState("");
  const [zoomImage, setZoomImage] = useState("");
  const gallery = useMemo(() => {
    const images = [product?.image, ...(product?.galleryImages || product?.gallery_images || [])].filter(Boolean);
    return [...new Set(images)];
  }, [product]);
  const currentImage = activeImage || gallery[0] || "/banner.png";

  if (!product) {
    return <EnterpriseShell content={content}><div className="mx-auto max-w-[1000px] px-4 py-16"><h1 className="text-3xl font-black">Product not found</h1></div></EnterpriseShell>;
  }

  const settings = content.settings || {};
  const productUrl = typeof window !== "undefined" ? window.location.href : `${API_BASE}/products/${product.slug}`;
  const whatsappMessage = (product.whatsappMessageTemplate || "").trim()
    || `Hello RP Infotech,\nI am interested in the following product:\n\nProduct: ${product.title}\nModel: ${product.modelNumber || "N/A"}\nPage: ${productUrl}\n\nPlease share pricing and availability.`;
  const inquiry = whatsappUrl(settings, whatsappMessage);
  const callHref = `tel:${String(settings.phone || "").replace(/\s+/g, "")}`;
  const shareProduct = async () => {
    try {
      if (navigator.share) await navigator.share({ title: product.title, url: productUrl });
      else {
        await navigator.clipboard?.writeText(productUrl);
        toast.success("Product link copied");
      }
    } catch {
      toast.error("Could not share product link");
    }
  };

  const badges = [product.availabilityStatus, ...(product.badges || [])].filter(Boolean);
  const specGroups = (product.specGroups || []).filter((group) => group && group.items && group.items.length);
  const variants = product.variants || [];
  const variantColumns = variants.length && product.variantColumns && product.variantColumns.length ? product.variantColumns : (variants[0] ? Object.keys(variants[0]) : []);
  const applications = product.applications || [];
  const installationSteps = product.installationSteps || [];
  const downloads = (product.downloads || []).filter((row) => row && row.url);
  const faqs = (product.faqs || []).filter((row) => row && row.question);
  const keyFeatures = product.keyFeatures && product.keyFeatures.length ? product.keyFeatures : (product.features || []).map((title) => ({ title, description: "" }));

  return (
    <EnterpriseShell content={content}>
      <ProductBreadcrumb product={product} />
      <section className="mx-auto grid max-w-[1400px] gap-8 px-4 py-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <div>
          <div className="relative aspect-[4/3] border border-slate-200 bg-slate-50">
            <img src={resolveWebsiteImage(currentImage)} alt={product.title} className="h-full w-full object-contain p-5" />
            <button onClick={() => setZoomImage(currentImage)} className="absolute bottom-4 right-4 rounded-sm bg-white px-3 py-2 text-sm font-black shadow"><ZoomIn className="mr-1 inline h-4 w-4" /> Zoom</button>
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {gallery.map((image) => (
                <button key={image} onClick={() => setActiveImage(image)} className={`aspect-square border bg-white p-1 ${currentImage === image ? "border-blue-600" : "border-slate-200"}`}>
                  <img src={resolveWebsiteImage(image)} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">{product.category}</span>
            {badges.map((badge) => <span key={badge} className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${availabilityBadgeClasses(badge)}`}>{badge}</span>)}
          </div>
          <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 lg:text-5xl">{product.title}</h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">{product.summary}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Info label="Model Number" value={product.modelNumber} />
            <Info label="Category" value={product.category} />
            <Info label="CTA / Price" value={product.priceText} />
            <Info label="Warranty" value={product.warrantyInformation} />
          </div>
          <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
            <a href={inquiry} target="_blank" rel="noreferrer" className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-green-600 px-5 py-3 font-black text-white">Request Quote on WhatsApp</a>
            <a href={callHref} className="inline-flex min-h-[52px] items-center justify-center rounded-sm border border-slate-200 px-5 py-3 font-black sm:hidden"><Phone className="mr-2 inline h-4 w-4" /> Call Now</a>
            <BrochureButton product={product} catalogueStyle />
            <button onClick={shareProduct} className="inline-flex min-h-[52px] items-center justify-center rounded-sm border border-slate-200 px-5 py-3 font-black"><Share2 className="mr-2 inline h-4 w-4" /> Share</button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-8 lg:px-8">
        <div className="grid gap-4 border border-green-200 bg-green-50 p-5 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.16em] text-green-700">Need price or stock confirmation?</div>
            <h2 className="mt-2 text-2xl font-black text-slate-950">Send this product to RP Infotech on WhatsApp.</h2>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">We can share pricing, installation options, compatible signage player details and demo timing.</p>
          </div>
          <a href={inquiry} target="_blank" rel="noreferrer" className="inline-flex min-h-[52px] items-center justify-center rounded-sm bg-green-600 px-5 py-3 font-black text-white">Continue on WhatsApp</a>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-8 px-4 pb-10 lg:grid-cols-[1fr_320px] lg:px-8">
        <div className="grid gap-6">
          <DetailSection title="Overview">
            <p className="leading-8 text-slate-700">{product.description || product.summary}</p>
            {product.industriesServed && product.industriesServed.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {product.industriesServed.map((industry) => <span key={industry} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{industry}</span>)}
              </div>
            )}
          </DetailSection>

          <DetailSection title="Key Features">
            {keyFeatures.length ? (
              <div className="grid gap-4 md:grid-cols-2">
                {keyFeatures.map((feature, index) => {
                  const title = typeof feature === "string" ? feature : feature.title;
                  const description = typeof feature === "string" ? "" : feature.description;
                  const Icon = iconMap[index % iconMap.length];
                  return (
                    <div key={`${title}-${index}`} className="flex gap-3 rounded-xl bg-slate-50 p-4">
                      <Icon className="h-5 w-5 flex-shrink-0 text-blue-600" />
                      <div>
                        <div className="font-black text-slate-900">{title}</div>
                        {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-slate-500">Feature details pending verification.</p>}
          </DetailSection>

          <DetailSection title="Technical Specifications">
            <SpecGroups groups={specGroups} legacy={product.technicalSpecifications || []} />
          </DetailSection>

          {variants.length > 0 && (
            <DetailSection title="Available Models / Variants">
              <VariantsTable columns={variantColumns} rows={variants} />
            </DetailSection>
          )}

          {applications.length > 0 && (
            <DetailSection title="Applications">
              <div className="grid gap-4 md:grid-cols-2">
                {applications.map((app) => (
                  <div key={app.title} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="font-black text-slate-900">{app.title}</div>
                    <p className="mt-1 text-sm text-slate-600">{app.description}</p>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {installationSteps.length > 0 && (
            <DetailSection title="Installation & Deployment">
              <ol className="grid gap-4 sm:grid-cols-2">
                {installationSteps.map((step, index) => (
                  <li key={step.title} className="rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2 text-sm font-black text-blue-700"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs text-white">{index + 1}</span>{step.title}</div>
                    <p className="mt-2 text-sm text-slate-600">{step.description}</p>
                  </li>
                ))}
              </ol>
            </DetailSection>
          )}

          <DetailSection title="Downloads">
            <div className="grid gap-3 sm:grid-cols-2">
              <BrochureButton product={product} outline />
              {downloads.map((file) => (
                <a key={file.url} href={resolveWebsiteImage(file.url)} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center gap-2 rounded-sm border border-slate-200 px-4 py-3 text-center text-sm font-black text-slate-800">
                  <Download className="h-4 w-4" /> {file.label || "Download"}
                </a>
              ))}
            </div>
            {!downloads.length && <p className="mt-3 text-sm text-slate-500">Additional documents will appear here once uploaded by our team.</p>}
          </DetailSection>

          {faqs.length > 0 && (
            <DetailSection title="Frequently Asked Questions">
              <div className="grid gap-3">
                {faqs.map((row) => (
                  <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <summary className="cursor-pointer font-black">{row.question}</summary>
                    <p className="mt-3 text-slate-600">{row.answer}</p>
                  </details>
                ))}
              </div>
            </DetailSection>
          )}

          {related.length > 0 && (
            <DetailSection title="Related Products">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {related.slice(0, 4).map((item) => <ProductCard key={item.slug} item={item} settings={content.settings} />)}
              </div>
            </DetailSection>
          )}
        </div>

        <aside className="grid h-max gap-6">
          <div className="sticky top-4 border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">Need This Product?</h2>
            <p className="mt-2 text-sm text-slate-500">Talk to RP Infotech for pricing, installation and demo.</p>
            <div className="mt-4 grid gap-2">
              <a href={inquiry} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center rounded-sm bg-green-600 px-4 py-3 text-sm font-black text-white">WhatsApp Enquiry</a>
              <Link to="/contact#demo" className="flex min-h-12 items-center justify-center rounded-sm bg-blue-600 px-4 py-3 text-sm font-black text-white">Book Live Demo</Link>
              <a href={callHref} className="flex min-h-12 items-center justify-center rounded-sm border border-slate-200 px-4 py-3 text-sm font-black text-slate-800"><Phone className="mr-2 h-4 w-4" /> Call Sales</a>
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <h2 className="text-lg font-black">QR Code</h2>
              <p className="mt-1 text-xs text-slate-500">Scan to open this live product page.</p>
              <img src={`${API_BASE}/public/website/products/${encodeURIComponent(product.slug)}/qr`} alt={`QR code for ${product.title}`} className="mt-3 aspect-square w-full border border-slate-100 object-contain p-4" />
              <a href={productUrl} className="mt-3 flex items-center gap-2 text-sm font-black text-blue-700"><ExternalLink className="h-4 w-4" /> Open product link</a>
            </div>
          </div>
        </aside>
      </section>

      {zoomImage && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={() => setZoomImage("")}>
          <button className="absolute right-4 top-4 text-white" aria-label="Close zoom"><X className="h-7 w-7" /></button>
          <img src={resolveWebsiteImage(zoomImage)} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 gap-2 border-t border-slate-200 bg-white/95 p-3 shadow-2xl backdrop-blur md:hidden">
        <a href={callHref} className="flex min-h-12 items-center justify-center rounded-sm border border-slate-300 px-2 text-xs font-black text-slate-800"><Phone className="h-4 w-4" /></a>
        <a href={inquiry} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center rounded-sm bg-green-600 px-2 text-xs font-black text-white">WhatsApp</a>
        <BrochureButton product={product} mobile />
      </div>
    </EnterpriseShell>
  );
}

function Info({ label, value }) {
  return <div className="border border-slate-200 bg-white p-3"><div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">{label}</div><div className="mt-1 font-black text-slate-950">{value || "-"}</div></div>;
}

function DetailSection({ title, children }) {
  return <section className="border border-slate-200 bg-white p-6 shadow-sm"><h2 className="text-2xl font-black tracking-tight text-slate-950">{title}</h2><div className="mt-4">{children}</div></section>;
}

function BulletList({ items = [] }) {
  if (!items.length) return <p className="text-slate-500">Not specified.</p>;
  return <div className="grid gap-3 md:grid-cols-2">{items.map((item) => <div key={item} className="border border-slate-100 bg-slate-50 p-3 font-semibold text-slate-700">{item}</div>)}</div>;
}

function SpecList({ rows = [] }) {
  if (!rows.length) return <p className="text-slate-500">Not specified.</p>;
  return <div className="grid gap-2 md:grid-cols-2">{rows.map((row, index) => {
    const text = String(row || "");
    const [label, ...rest] = text.includes(":") ? text.split(":") : [`Specification ${index + 1}`, text];
    return <Info key={`${text}-${index}`} label={label.trim()} value={rest.join(":").trim()} />;
  })}</div>;
}

function BrochureButton({ product, compact = false, catalogueStyle = false, outline = false, mobile = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const mode = String(product?.brochureDownloadMode || "dealer_only");
  const isPrivate = mode === "private";
  const canLikelyDownload = mode === "public" || user?.role === "dealer" || user?.role === "admin" || user?.brochure_permission || user?.can_download_brochures;

  if (isPrivate) {
    if (mobile) return <button className="flex min-h-12 items-center justify-center rounded-sm bg-slate-300 px-3 text-sm font-black text-slate-600" disabled>No Brochure</button>;
    return compact ? null : <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-300">Brochure download is private for this product.</div>;
  }

  const download = async () => {
    if (!canLikelyDownload) {
      toast.error("Dealer login is required to download this brochure.");
      return;
    }
    try {
      setBusy(true);
      const res = await api.get(`/public/website/products/${product.slug}/brochure`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${product.slug || "product"}-brochure.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Brochure downloaded");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      className={`${mobile ? "flex min-h-12 items-center justify-center rounded-sm bg-slate-950 px-3 text-sm text-white" : outline ? "min-h-12 rounded-sm border border-slate-200 px-4 py-3 text-slate-950" : catalogueStyle ? "inline-flex min-h-[52px] items-center justify-center rounded-sm bg-slate-950 px-5 py-3 text-white" : compact ? "mt-4 w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 hover:bg-blue-100" : "inline-flex items-center justify-center rounded-xl border border-white/20 bg-white px-5 py-3 text-[#07142f] hover:bg-slate-100"} font-black transition disabled:cursor-not-allowed disabled:opacity-70`}
    >
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
      Download Brochure
    </button>
  );
}

function Testimonials({ rows = [] }) {
  return <section className="bg-slate-50"><div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8"><h2 className="text-3xl font-black">Trusted by Businesses</h2><div className="mt-6 grid gap-5 md:grid-cols-2">{publicItems(rows).map((row) => <div key={`${row.name}-${row.business}`} className="rounded-2xl bg-white p-6 shadow-sm"><div className="flex text-orange-400">{Array.from({ length: Number(row.rating || 5) }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div><p className="mt-4 text-slate-700">{row.review}</p><div className="mt-4 font-black">{row.name}</div><div className="text-sm text-slate-500">{row.business} - {row.industry}</div></div>)}</div></div></section>;
}

function Faq({ rows = [] }) {
  return <section className="mx-auto max-w-[1000px] px-4 py-12 lg:px-8"><h2 className="text-3xl font-black">FAQ</h2><div className="mt-6 grid gap-3">{publicItems(rows).map((row) => <details key={row.question} className="rounded-2xl border border-slate-200 bg-white p-5"><summary className="cursor-pointer font-black">{row.question}</summary><p className="mt-3 text-slate-600">{row.answer}</p></details>)}</div></section>;
}

function Cta({ settings }) {
  return <section className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8"><div className="rounded-3xl bg-gradient-to-r from-blue-700 to-[#07142f] p-8 text-white lg:p-12"><h2 className="text-4xl font-black">Ready to Modernize Your Business?</h2><p className="mt-3 max-w-2xl text-slate-200">Book a free demo for LED TVs, LED active display video walls, interactive panels, digital signage software, token systems or QR ordering.</p><div className="mt-6 flex flex-wrap gap-3"><Link to="/contact#demo" className="rounded-xl bg-orange-500 px-5 py-3 font-black">Book Free Demo</Link><a href={whatsappUrl(settings)} className="rounded-xl border border-white/30 px-5 py-3 font-black">Talk to Expert</a></div></div></section>;
}
